import { Stack, Box, Group, Text, Title, LoadingOverlay, Divider, Table, TypographyStylesProvider } from '@mantine/core'
import { LineChart } from '@mantine/charts'
import {
  IconCurrencyDollar,
  IconReceiptDollar,
  IconChartLine,
  IconReportMoney,
  IconCalendarStats,
  IconTrendingUp,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'

import { SmallBox, WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { CostByAccountCard } from '@/components/finops/widgets/CostByAccountCard'
import { CostByServiceCard } from '@/components/finops/widgets/CostByServiceCard'
import { TrendCard } from '@/components/finops/widgets/TrendCard'
import { finopsApi } from '@/api/finops'
import type { BreakdownResponse, TrendResponse } from '@/api/finopsDashboard'
import { formatUSD, formatPct } from '@/utils/finops'

const TOP_ACCOUNTS = 10

function compactUSD(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}k`
  return `$${value.toFixed(0)}`
}

export function RelatorioDetalheV2() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const reportId = params.id ? Number.parseInt(params.id, 10) : NaN

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['finops', 'report', reportId, TOP_ACCOUNTS],
    queryFn: () => finopsApi.getReport(reportId, TOP_ACCOUNTS),
    enabled: Number.isFinite(reportId),
  })

  const breadcrumbs = [
    { label: 'FinOps', path: '/finops' },
    { label: 'Relatórios', path: '/finops/reports' },
    { label: report ? `Semana ${report.week}` : '...' },
  ]

  if (!Number.isFinite(reportId)) {
    return (
      <Stack gap="lg">
        <EmptyState
          icon={IconReportMoney}
          title="ID inválido"
          description="O ID do relatório na URL é inválido."
          accent="warning"
          primaryAction={{
            label: 'Voltar para lista',
            onClick: () => navigate('/finops/reports'),
          }}
        />
      </Stack>
    )
  }

  if (isError) {
    return (
      <Stack gap="lg">
        <EmptyState
          icon={IconReportMoney}
          title="Relatório não encontrado"
          description="Esse relatório pode ter sido removido ou você não tem acesso ao tenant correspondente."
          accent="warning"
          primaryAction={{
            label: 'Voltar para lista',
            onClick: () => navigate('/finops/reports'),
          }}
        />
      </Stack>
    )
  }

  // Adapta o payload do relatório (S5-A) para os shapes dos widgets SP3
  // (BreakdownResponse/TrendResponse), reusando os widgets Mantine extraídos.
  const accountBreakdown: BreakdownResponse = {
    group_by: 'account',
    items: (report?.cost_by_account ?? []).map(a => ({
      key: a.account_name || a.account_id,
      amount_usd: Number(a.cost_usd ?? 0),
      pct: 0,
    })),
    as_of: report?.period_end ?? '',
    is_partial: false,
    cost_available: true,
  }

  const serviceBreakdown: BreakdownResponse = {
    group_by: 'service',
    items: (report?.cost_by_service ?? []).map(s => ({
      key: s.service,
      amount_usd: Number(s.cost_usd ?? 0),
      pct: 0,
    })),
    as_of: report?.period_end ?? '',
    is_partial: false,
    cost_available: true,
  }

  const monthlyTrend: TrendResponse = {
    points: (report?.monthly_trend ?? []).map(t => ({
      period_start: t.month,
      amount_usd: Number(t.total_usd ?? 0),
      is_partial: false,
    })),
    mom_pct: null,
    yoy_pct: null,
    as_of: report?.period_end ?? '',
    is_partial: false,
    cost_available: true,
  }

  const weeklyData = (report?.weekly_total ?? []).map(w => ({
    week: w.week,
    Total: Number(w.total_usd ?? 0),
  }))

  const topMovers = report?.top_movers ?? []

  const sp = report?.savings_plans ?? null

  return (
    <Stack gap="lg">

      <Box style={{ position: 'relative', minHeight: 200 }}>
        <LoadingOverlay visible={isLoading} zIndex={2} />

        {report && (
          <Stack gap="lg">
            {/* Header info */}
            <Group gap="md">
              <Group gap={6}>
                <IconCalendarStats size={16} color="var(--ai-text-muted)" />
                <Text size="xs" c="dimmed">
                  Período:{' '}
                  <Text component="span" size="xs" c="var(--ai-text-secondary)" fw={500}>
                    {report.period_start ?? '—'} → {report.period_end ?? '—'}
                  </Text>
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Gerado em{' '}
                <Text component="span" size="xs" c="var(--ai-text-secondary)" fw={500}>
                  {new Date(report.generated_at).toLocaleString('pt-BR')}
                </Text>
              </Text>
            </Group>

            {/* KPIs */}
            <Box
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 16,
              }}
            >
              <SmallBox
                value={formatUSD(report.total_cost_usd)}
                label="Custo total do período"
                icon={<IconCurrencyDollar size={48} stroke={1.5} />}
                color="primary"
                info="Soma de unblended_cost para o período do relatório, conforme payload do data.json."
              />
              <SmallBox
                value={formatPct(sp?.utilization_pct ?? null)}
                label="SP Utilization"
                icon={<IconChartLine size={48} stroke={1.5} />}
                color="info"
                info="Percentual de utilização dos Savings Plans no período. null se SP não está provisionado."
              />
              <SmallBox
                value={formatUSD(sp?.net_savings_usd ?? null)}
                label="SP Net Savings"
                icon={<IconReceiptDollar size={48} stroke={1.5} />}
                color="success"
                info="Economia líquida frente ao on-demand equivalent calculada pelo gerador do report."
              />
            </Box>

            {/* Charts */}
            <Box
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
                gap: 16,
              }}
            >
              <CostByAccountCard data={accountBreakdown} title="Custo por conta AWS" />

              <TrendCard data={monthlyTrend} title="Tendência mensal" />
            </Box>

            <Divider />

            {/* S5-A: Tendência semanal (12 semanas) */}
            <WidgetCard accent="info" p={0}>
              <CardHeader title="Tendência semanal (últimas 12 semanas)" />
              <Box p="md">
                {weeklyData.length === 0 ? (
                  <EmptyState
                    icon={IconChartLine}
                    title="Sem dados semanais"
                    description="O payload deste relatório não contém série semanal."
                    accent="info"
                    size="sm"
                    bare
                  />
                ) : (
                  <LineChart
                    h={240}
                    data={weeklyData}
                    dataKey="week"
                    series={[{ name: 'Total', color: 'inovareTeal.7' }]}
                    curveType="monotone"
                    tickLine="xy"
                    gridAxis="xy"
                    withDots
                    valueFormatter={v => compactUSD(Number(v))}
                  />
                )}
              </Box>
            </WidgetCard>

            {/* S5-A: Top movers (week-over-week) */}
            <WidgetCard accent="warning" p={0}>
              <CardHeader title="Top variações de custo (semana atual vs anterior)" />
              <Box p="md">
                {topMovers.length === 0 ? (
                  <EmptyState
                    icon={IconTrendingUp}
                    title="Sem variações"
                    description="Precisamos de pelo menos 2 semanas de dados para comparativo."
                    accent="info"
                    size="sm"
                    bare
                  />
                ) : (
                  <Table striped withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Conta</Table.Th>
                        <Table.Th>Semana anterior</Table.Th>
                        <Table.Th>Semana atual</Table.Th>
                        <Table.Th>Δ US$</Table.Th>
                        <Table.Th>Δ %</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {topMovers.map(m => (
                        <Table.Tr key={m.account_id}>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {m.account_name || m.account_id}
                            </Text>
                            {m.account_name && (
                              <Text size="xs" c="dimmed" ff="monospace">
                                {m.account_id}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>{formatUSD(m.prev_usd)}</Table.Td>
                          <Table.Td>{formatUSD(m.curr_usd)}</Table.Td>
                          <Table.Td>
                            <Text c={m.delta_usd > 0 ? 'red.6' : 'green.6'} fw={600} size="sm">
                              {m.delta_usd > 0 ? '+' : ''}
                              {formatUSD(m.delta_usd)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {m.delta_pct != null ? (
                              <Text c={m.delta_pct > 0 ? 'red.6' : 'green.6'} size="sm">
                                {formatPct(m.delta_pct, { signed: true })}
                              </Text>
                            ) : (
                              '—'
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Box>
            </WidgetCard>

            {/* S5-A: Custo por serviço AWS (último mês) */}
            <CostByServiceCard data={serviceBreakdown} title="Custo por serviço AWS (último mês)" />


            <Divider />

            {/* Summary markdown */}
            <WidgetCard accent="primary" p={0}>
              <CardHeader title="Resumo executivo" />
              <Box p="md">
                {report.summary_md ? (
                  <TypographyStylesProvider>
                    <ReactMarkdown>{report.summary_md}</ReactMarkdown>
                  </TypographyStylesProvider>
                ) : (
                  <EmptyState
                    icon={IconReportMoney}
                    title="Sem resumo executivo"
                    description="O summary.md não foi gerado ou ainda não foi ingerido para este relatório."
                    accent="info"
                    size="sm"
                    bare
                  />
                )}
              </Box>
            </WidgetCard>

            {/* Footer: refs S3 */}
            {(report.data_s3_uri || report.summary_s3_uri) && (
              <Stack gap={4}>
                {report.data_s3_uri && (
                  <Text size="xs" c="dimmed">
                    data.json: <Text component="span" ff="monospace" size="xs">{report.data_s3_uri}</Text>
                  </Text>
                )}
                {report.summary_s3_uri && (
                  <Text size="xs" c="dimmed">
                    summary.md: <Text component="span" ff="monospace" size="xs">{report.summary_s3_uri}</Text>
                  </Text>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {!isLoading && !report && !isError && (
          <Box>
            <Title order={4}>—</Title>
          </Box>
        )}
      </Box>
    </Stack>
  )
}
